import styled from "styled-components";
import { Link } from "react-router-dom";
import { Menu } from "antd";
import { COLORS } from "../../styled/basic";

/** Matches global mobile layout breakpoint (see index.tsx GlobalStyle). */
export const NAV_MOBILE_MAX_PX = 639;

export const StyledLink = styled(Link)`
  display: flex;
  flex-shrink: 0;
  align-items: stretch;
  align-self: stretch;
  height: 100%;
  text-decoration: none;

  &:focus, &:hover, &:visited, &:link, &:active {
    text-decoration: none;
  }
`;

export const DesktopNavGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${NAV_MOBILE_MAX_PX + 1}px) {
    /* Occupy all space between logo and profile; horizontal scroll stays inside fixed 56px bar */
    flex: 1 1 0;
    align-self: stretch;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.35) transparent;

    &::-webkit-scrollbar {
      height: 5px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 3px;
    }
  }

  @media (max-width: ${NAV_MOBILE_MAX_PX}px) {
    display: none;
  }
`;

export const DesktopAvatarWrap = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  flex: 0 0 auto;
  height: 100%;
  max-width: min(32vw, 260px);
  min-width: 0;

  @media (max-width: ${NAV_MOBILE_MAX_PX}px) {
    display: none;
  }
`;

export const MobileMenuWrap = styled.div`
  display: none;
  align-items: center;
  align-self: center;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: ${NAV_MOBILE_MAX_PX}px) {
    display: flex;
  }
`;

export const HamburgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.BLUE_MAIN};
    outline-offset: 2px;
  }
`;

export const MobileDrawerLink = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 14px 20px;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(10, 124, 178, 0.1)" : "transparent")};
  color: ${COLORS.BLACK_MAIN};
  font-family: "Montserrat", sans-serif;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  border-left: 3px solid ${({ $active }) => ($active ? COLORS.BLUE_MAIN : "transparent")};

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const MobileDrawerMuted = styled.div`
  padding: 10px 20px 6px;
  font-size: 12px;
  color: #6b7280;
  word-break: break-all;
  font-family: "Montserrat", sans-serif;
`;

export const NavigationBlock = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  height: 56px;
  min-height: 56px;
  max-height: 56px;
  background-color: ${COLORS.BLACK_MAIN};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0 16px;
  user-select: none;
  border-bottom: 2px solid ${COLORS.BLUE_MAIN};
  gap: 0;
  overflow-x: hidden;
  overflow-y: hidden;
  box-sizing: border-box;

  @media (max-width: ${NAV_MOBILE_MAX_PX}px) {
    padding: 0 12px;
    align-items: center;
  }
`;

interface NavigationItemProps {
  active?: boolean;
}

export const NavigationItem = styled.div<NavigationItemProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ active }) => (active ? COLORS.WHITE : "rgba(255, 255, 255, 0.75)")};
  height: 100%;
  min-width: 72px;
  padding: 0 10px;
  flex-shrink: 0;
  white-space: nowrap;
  box-sizing: border-box;
  border-bottom: 3px solid ${({ active }) => (active ? COLORS.BLUE_MAIN : "transparent")};
  margin-bottom: -2px;

  @media (min-width: ${NAV_MOBILE_MAX_PX + 1}px) {
    min-width: 80px;
    padding: 0 12px;
    font-size: 12px;
    letter-spacing: 0.3px;
  }

  &:hover {
    color: ${COLORS.WHITE};
    background: rgba(255, 255, 255, 0.08);
  }

  transition: color 0.2s, background 0.2s, border-color 0.2s;
`;

export const NavDropdownTrigger = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;
  flex-shrink: 0;
  cursor: pointer;
`;

/** Elevated menu panel for top-nav hover dropdowns. */
export const NavDropdownMenu = styled(Menu)`
  && {
    min-width: 240px;
    padding: 8px;
    margin-top: 6px;
    border-radius: 12px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow:
      0 12px 28px rgba(15, 23, 42, 0.14),
      0 2px 6px rgba(15, 23, 42, 0.06);
    background: #fff;
  }

  && .ant-dropdown-menu-item {
    padding: 0;
    margin: 2px 0;
    border-radius: 8px;
    line-height: 1.2;
  }

  && .ant-dropdown-menu-item:hover,
  && .ant-dropdown-menu-item-active {
    background: rgba(10, 124, 178, 0.1);
  }

  && .ant-dropdown-menu-item-selected {
    background: rgba(10, 124, 178, 0.14);
  }
`;

export const NavDropdownItemRow = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  width: 100%;
  box-sizing: border-box;
`;

export const NavDropdownItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(10, 124, 178, 0.14), rgba(10, 124, 178, 0.06));
  color: ${COLORS.BLUE_MAIN};
  font-size: 15px;
`;

export const NavDropdownItemLabel = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: 0.01em;
`;

export const MobileDrawerLinkWithIcon = styled(MobileDrawerLink)`
  display: flex;
  align-items: center;
  gap: 12px;

  > span:first-child {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: rgba(10, 124, 178, 0.1);
    color: ${COLORS.BLUE_MAIN};
    font-size: 14px;
    flex-shrink: 0;
  }
`;

export const AvatarBlock = styled.div`
  display: flex;
  flex: 0 0 auto;
  min-width: 0;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

export const AvatarArea = styled.div`
  width: 36px;
  height: 36px;
  cursor: pointer;
`;

/** Bell control next to avatar; notification count uses antd Badge (blue / white). */
export const NotificationBellButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 4px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${COLORS.BLUE_MAIN};
    outline-offset: 2px;
  }
`;
